import { useState } from 'react';
import { toast } from 'sonner';

import { PageHeader, EditableFormSection, SectionCard, ReadOnlyValue } from '@/components/shared/DashboardUI';
import { apiPut, apiPost } from '@/lib/api';
import { formatPhoneNumber, parsePhoneNumber, formatZipCode, US_STATES, formatDateOnly } from '@/lib/formatters';
import '@/styles/authenticated.css';

/* ================================================================
   Shared Profile Tab
   ----------------------------------------------------------------
   One component for clients, lawyers and funders. Previously each
   dashboard carried its own near-identical copy, which is how three
   different versions of the same form ended up with no validation
   between them.

   Role only changes the organisation field: lawyers have a firm,
   funders have a company, clients have neither.
   ================================================================ */

const MIN_PASSWORD_LENGTH = 8;

// Mirrors backend/auth_email.py — the server rejects these too. Checking here
// as well keeps a doomed request off the wire and puts the message next to the
// field instead of in a toast.
const ORG_LABEL = { lawyer: 'Firm name', funder: 'Company name' };

const initialsOf = (name) => (name || '')
  .split(' ')
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0].toUpperCase())
  .join('') || '?';

// The same rule the application forms store against, so a ZIP saved here and a
// ZIP saved on an application have the same shape: 12345 or 12345-6789.
const COMPLETE_ZIP = /^\d{5}(-\d{4})?$/;
const isCompleteZip = (zip) => COMPLETE_ZIP.test(zip);

const ProfileTab = ({ user, onUpdate }) => {
  const orgLabel = ORG_LABEL[user?.role];

  const [formData, setFormData] = useState({
    name: user?.name || '',
    company_name: user?.company_name || '',
    phone: user?.phone || '',
    address1: user?.address1 || '',
    address2: user?.address2 || '',
    city: user?.city || '',
    state: user?.state || '',
    zip: user?.zip || '',
  });

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [zipError, setZipError] = useState('');

  const setField = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      company_name: user?.company_name || '',
      phone: user?.phone || '',
      address1: user?.address1 || '',
      address2: user?.address2 || '',
      city: user?.city || '',
      state: user?.state || '',
      zip: user?.zip || '',
    });
    setZipError('');
    setEditing(null);
  };

  const handleSave = async (section) => {
    if (section === 'address' && formData.zip && !isCompleteZip(formData.zip)) {
      setZipError('Enter a 5 or 9 digit ZIP code.');
      return;
    }
    setZipError('');
    setSaving(true);
    try {
      await apiPut('/users/me', formData);
      toast.success('Profile updated');
      setEditing(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.message || 'Could not save your profile');
    } finally {
      setSaving(false);
    }
  };

  const location = [formData.city, formData.state].filter(Boolean).join(', ');

  return (
    <div data-testid="profile-tab">
      <PageHeader title="Profile" subtitle="Manage your account information" />

      <div className="mcl-profile-layout">
        <aside className="mcl-profile-rail" data-testid="profile-rail">
          <div className="mcl-profile-avatar" aria-hidden="true">{initialsOf(user?.name)}</div>
          <div>
            <div className="mcl-profile-rail-name">{user?.name || 'Unnamed account'}</div>
            <span className="mcl-profile-role">{user?.role || 'member'}</span>
          </div>
          <dl className="mcl-profile-rail-list">
            <div className="mcl-profile-rail-item">
              <dt>Email</dt>
              <dd>{user?.email || '—'}</dd>
            </div>
            <div className="mcl-profile-rail-item">
              <dt>Location</dt>
              <dd>{[location, formData.zip].filter(Boolean).join(' ') || '—'}</dd>
            </div>
            {user?.created_at && (
              <div className="mcl-profile-rail-item">
                <dt>Member since</dt>
                <dd>{formatDateOnly(user.created_at)}</dd>
              </div>
            )}
          </dl>
        </aside>

        <div className="mcl-profile-main">
          <EditableFormSection
            title="Personal information"
            sectionKey="personal"
            isEditing={editing === 'personal'}
            isSaving={saving}
            onEditClick={() => setEditing('personal')}
            onSaveClick={() => handleSave('personal')}
            onCancelClick={handleCancel}
          >
            {editing === 'personal' ? (
              <div className="mcl-form-row">
                <div className="mcl-form-group">
                  <label className="mcl-form-label" htmlFor="profile-name">Full name</label>
                  <input
                    id="profile-name"
                    className="mcl-form-input"
                    value={formData.name}
                    onChange={(e) => setField('name', e.target.value)}
                    data-testid="input-name"
                  />
                </div>
                {orgLabel && (
                  <div className="mcl-form-group">
                    <label className="mcl-form-label" htmlFor="profile-company">{orgLabel}</label>
                    <input
                      id="profile-company"
                      className="mcl-form-input"
                      value={formData.company_name}
                      onChange={(e) => setField('company_name', e.target.value)}
                      data-testid="input-company-name"
                    />
                  </div>
                )}
                <div className="mcl-form-group">
                  <label className="mcl-form-label" htmlFor="profile-email">Email</label>
                  <input id="profile-email" className="mcl-form-input" value={user?.email || ''} disabled />
                  <div className="mcl-form-hint">Contact support to change your email.</div>
                </div>
                <div className="mcl-form-group">
                  <label className="mcl-form-label" htmlFor="profile-phone">Phone</label>
                  <input
                    id="profile-phone"
                    className="mcl-form-input"
                    value={formatPhoneNumber(formData.phone)}
                    onChange={(e) => setField('phone', parsePhoneNumber(e.target.value))}
                    placeholder="(555) 123-4567"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            ) : (
              <div className="mcl-form-row">
                <div className="mcl-form-group">
                  <span className="mcl-form-label">Full name</span>
                  <ReadOnlyValue value={formData.name} />
                </div>
                {orgLabel && (
                  <div className="mcl-form-group">
                    <span className="mcl-form-label">{orgLabel}</span>
                    <ReadOnlyValue value={formData.company_name} />
                  </div>
                )}
                <div className="mcl-form-group">
                  <span className="mcl-form-label">Email</span>
                  <ReadOnlyValue value={user?.email} />
                </div>
                <div className="mcl-form-group">
                  <span className="mcl-form-label">Phone</span>
                  <ReadOnlyValue value={formatPhoneNumber(formData.phone)} />
                </div>
              </div>
            )}
          </EditableFormSection>

          <EditableFormSection
            title="Address"
            sectionKey="address"
            isEditing={editing === 'address'}
            isSaving={saving}
            onEditClick={() => setEditing('address')}
            onSaveClick={() => handleSave('address')}
            onCancelClick={handleCancel}
          >
            {editing === 'address' ? (
              <>
                <div className="mcl-form-group">
                  <label className="mcl-form-label" htmlFor="profile-address1">Street address</label>
                  <input
                    id="profile-address1"
                    className="mcl-form-input"
                    value={formData.address1}
                    onChange={(e) => setField('address1', e.target.value)}
                    data-testid="input-address1"
                  />
                </div>
                <div className="mcl-form-group">
                  <label className="mcl-form-label" htmlFor="profile-address2">Apartment, suite, unit</label>
                  <input
                    id="profile-address2"
                    className="mcl-form-input"
                    value={formData.address2}
                    onChange={(e) => setField('address2', e.target.value)}
                    placeholder="Optional"
                    data-testid="input-address2"
                  />
                </div>
                <div className="mcl-form-row mcl-form-row-city-state-zip">
                  <div className="mcl-form-group">
                    <label className="mcl-form-label" htmlFor="profile-city">City</label>
                    <input
                      id="profile-city"
                      className="mcl-form-input"
                      value={formData.city}
                      onChange={(e) => setField('city', e.target.value)}
                      data-testid="input-city"
                    />
                  </div>
                  <div className="mcl-form-group">
                    <label className="mcl-form-label" htmlFor="profile-state">State</label>
                    <select
                      id="profile-state"
                      className="mcl-form-input"
                      value={formData.state}
                      onChange={(e) => setField('state', e.target.value)}
                      data-testid="input-state"
                    >
                      <option value="">Select a state</option>
                      {US_STATES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mcl-form-group">
                    <label className="mcl-form-label" htmlFor="profile-zip">ZIP code</label>
                    <input
                      id="profile-zip"
                      className={`mcl-form-input${zipError ? ' is-invalid' : ''}`}
                      inputMode="numeric"
                      value={formData.zip}
                      onChange={(e) => {
                        setField('zip', formatZipCode(e.target.value));
                        setZipError('');
                      }}
                      placeholder="12345"
                      data-testid="input-zip"
                    />
                    {zipError
                      ? <div className="mcl-form-error" data-testid="zip-error">{zipError}</div>
                      : <div className="mcl-form-hint">Digits only.</div>}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="mcl-form-group">
                  <span className="mcl-form-label">Street address</span>
                  <ReadOnlyValue value={[formData.address1, formData.address2].filter(Boolean).join(', ')} />
                </div>
                <div className="mcl-form-row mcl-form-row-city-state-zip">
                  <div className="mcl-form-group">
                    <span className="mcl-form-label">City</span>
                    <ReadOnlyValue value={formData.city} />
                  </div>
                  <div className="mcl-form-group">
                    <span className="mcl-form-label">State</span>
                    <ReadOnlyValue value={formData.state} />
                  </div>
                  <div className="mcl-form-group">
                    <span className="mcl-form-label">ZIP code</span>
                    <ReadOnlyValue value={formData.zip} />
                  </div>
                </div>
              </>
            )}
          </EditableFormSection>

          <ChangePasswordSection />
        </div>
      </div>
    </div>
  );
};

/* ---- Security ----
   A password change is one deliberate act, not an editable record, so this
   stays a plain form rather than reusing EditableFormSection. */
const ChangePasswordSection = () => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fields, setFields] = useState({ current: '', next: '', confirm: '' });

  const setField = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setError('');
  };

  const close = () => {
    setFields({ current: '', next: '', confirm: '' });
    setError('');
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!fields.current) {
      setError('Enter your current password.');
      return;
    }
    if (fields.next.length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (fields.next !== fields.confirm) {
      setError('New passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await apiPost('/auth/change-password', {
        current_password: fields.current,
        new_password: fields.next,
      });
      toast.success('Password updated');
      close();
    } catch (err) {
      setError(err.message || 'Could not update your password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Security"
      actions={
        <button
          type="button"
          className="mcl-btn mcl-btn-sm mcl-btn-outline-primary"
          onClick={() => (open ? close() : setOpen(true))}
          data-testid="change-password-button"
        >
          {open ? 'Cancel' : 'Change password'}
        </button>
      }
    >
      {open ? (
        <div className="mcl-form-row">
          <div className="mcl-form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="mcl-form-label" htmlFor="profile-current-password">Current password</label>
            <input
              id="profile-current-password"
              className="mcl-form-input"
              type="password"
              autoComplete="current-password"
              value={fields.current}
              onChange={(e) => setField('current', e.target.value)}
              data-testid="input-current-password"
            />
          </div>
          <div className="mcl-form-group">
            <label className="mcl-form-label" htmlFor="profile-new-password">New password</label>
            <input
              id="profile-new-password"
              className="mcl-form-input"
              type="password"
              autoComplete="new-password"
              value={fields.next}
              onChange={(e) => setField('next', e.target.value)}
              data-testid="input-new-password"
            />
            <div className="mcl-form-hint">At least {MIN_PASSWORD_LENGTH} characters.</div>
          </div>
          <div className="mcl-form-group">
            <label className="mcl-form-label" htmlFor="profile-confirm-password">Confirm new password</label>
            <input
              id="profile-confirm-password"
              className="mcl-form-input"
              type="password"
              autoComplete="new-password"
              value={fields.confirm}
              onChange={(e) => setField('confirm', e.target.value)}
              data-testid="input-confirm-password"
            />
          </div>
          {error && (
            <div className="mcl-form-error" style={{ gridColumn: '1 / -1' }} data-testid="password-error">
              {error}
            </div>
          )}
          <div style={{ gridColumn: '1 / -1' }}>
            <button
              type="button"
              className="mcl-btn mcl-btn-primary"
              onClick={handleSubmit}
              disabled={saving}
              data-testid="submit-password-button"
            >
              {saving ? 'Updating...' : 'Update password'}
            </button>
          </div>
        </div>
      ) : (
        <div className="mcl-form-row">
          <div className="mcl-form-group">
            <span className="mcl-form-label">Password</span>
            <ReadOnlyValue value="••••••••••" />
          </div>
        </div>
      )}
    </SectionCard>
  );
};

export { ProfileTab };
export default ProfileTab;
